import { defineStore } from 'pinia';
import type { RouteLocationNormalized } from 'vue-router';
import { connection } from '@vp/vue-components';
import { API } from '@vp/common';

export interface RootState {
  route?: RouteLocationNormalized;
  currentAccountId: number;
  passkeyLoginEnabled: boolean;
  _apiBase?: string;
  _apiGraphqlEndpoint?: string;
  defaultApiBase?: string;
  config?: API.VPConfig.VPConfig;
}

interface VPAppConfig {
  apiBase: string;
  apiGraphqlEndpoint?: string;
  currentAccountId: number;
  passkeyLoginEnabled?: boolean;
}

declare global {
  interface Window {
    vpAppConfig?: VPAppConfig;
  }
}

const getApiBase = (): string | undefined => {
  const appConfig = !import.meta.env.SSR ? window.vpAppConfig : undefined;

  if (appConfig?.apiBase) {
    return appConfig.apiBase;
  } else {
    console.warn('missing window.vpAppConfig.apiBase ');
  }

  return '/api';
};

const API_BASE_KEY = 'vp-checkout-api-base';

const getStoredApiBase = (): string | undefined => {
  if (typeof window !== 'undefined') {
    return sessionStorage.getItem(API_BASE_KEY) || undefined;
  }
};

const storeApiBase = (address?: string) => {
  if (typeof window !== 'undefined') {
    if (address) {
      sessionStorage.setItem(API_BASE_KEY, address);
    } else {
      sessionStorage.removeItem(API_BASE_KEY);
    }
  }
};

const getGqlApiEndpoint = (): string | undefined => {
  const appConfig = !import.meta.env.SSR ? window.vpAppConfig : undefined;

  if (appConfig?.apiGraphqlEndpoint) {
    return appConfig.apiGraphqlEndpoint;
  } else {
    console.warn('missing window.vpAppConfig.gqlApiEndpoint');
  }

  return import.meta.env.VITE_GQL_API_ENDPOINT;
};

export const useRootStore = defineStore('root', {
  state: (): RootState => ({
    route: undefined,
    _apiBase: getStoredApiBase(),
    defaultApiBase: getApiBase(),
    _apiGraphqlEndpoint: getGqlApiEndpoint(),
    currentAccountId: (typeof window !== 'undefined' ? window.vpAppConfig?.currentAccountId : null) || 0,
    passkeyLoginEnabled: (typeof window !== 'undefined' ? window.vpAppConfig?.passkeyLoginEnabled : false) || false,
  }),
  getters: {
    apiBase(state: RootState): string {
      if (typeof window === 'undefined') {
        return process.env.CHECKOUT_API_URL!;
      }

      const base = state._apiBase || state.defaultApiBase || '';
      return base;
    },

    apiGraphqlEndpoint(state: RootState): string | undefined {
      // TODO
      return state._apiGraphqlEndpoint ?? `${this.apiBase}/graphql`;
    },

    isDefaultApiBase(state: RootState): boolean {
      return this.apiBase == state.defaultApiBase || !state.defaultApiBase;
    },
  },
  actions: {
    SET_CURRENT_ROUTE(route: RouteLocationNormalized) {
      this.route = route;
    },

    setApiBase(address: string | undefined) {
      // console.debug('setting server address to:', address)
      this._apiBase = address;
      storeApiBase(address);
    },

    async loadConfig() {
      const connectionStore = connection.useConnectionStore();
      const apiClient = connectionStore.apiClient;

      if (!apiClient) {
        throw new Error('apiClient not initialized');
      }
      this.config = await API.getConfig(apiClient);
    },
  },
});
